import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Button,
  TextField,
  Tooltip,
  Container,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import CheckIcon from "@mui/icons-material/Check";
import VendorValidationHelper from "@/app/helpers/VendorRules";
import ContractorData from "@/app/interfaces/response/ContractorResponse";
import dataService from "@/app/services/dataService";
import UnenrollModal from "../modal/UnenrollModal";
import SimpleModal from "../shared/simpleModal";
import PageLoadingOverlay from "../shared/pageLoadingOverlay";
import { Column } from "@/app/interfaces/Column";
import { ERROR_MESSAGE, SHORT_CASHING } from "@/app/constants/constants";
import { GenericDataDisplayTypeEnum } from "@/enums/genericDataDisplayType";
import defaultImage from "@/img/default-image_210.png";
import GenericDataDisplay from "../shared/genericDataDisplay";

const STYLES = {
  orangeGradient: "linear-gradient(to bottom, #ffc556 0%, #ff9a00 100%)",
  lightGrey: "#f5f5f5",
  borderGrey: "#ccc",
  linkBlue: "#58a6d1",
};

const VALIDATION_IDS_MESSAGE =
  "Contractor Id is already in use for other customer. Please correct the format and try again.";

const EnrollmentTable = () => {
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [currentInput, setCurrentInput] = useState<string>("");
  const [programToUnenroll, setProgramToUnenroll] =
    useState<ContractorData | null>(null);
  const [errorModal, setErrorModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  } | null>(null);

  const updateMutation = useMutation({
    mutationFn: (payload: ContractorData) =>
      dataService.updateContractorData(payload),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ["contractorEnrollments"] });
        setEditingId(null);
        setProgramToUnenroll(null);
      } else {
        setErrorModal({
          isOpen: true,
          title: "Update Error",
          message: ERROR_MESSAGE,
        });
      }
    },
  });

  const handleUpdateSubmit = (program: ContractorData) => {
    const trimmedValue = currentInput.trim();

    const formatErrorMsg = VendorValidationHelper.validate(
      program.vendor.program,
      trimmedValue
    );

    if (formatErrorMsg) {
      setErrorModal({
        isOpen: true,
        title: "Validation Error",
        message: formatErrorMsg,
      });

      return;
    }

    if (program.existingContractorIds) {
      const idList = program.existingContractorIds
        .split(",")
        .map((id) => id.trim());

      if (idList.includes(trimmedValue)) {
        setErrorModal({
          isOpen: true,
          title: "VALIDATION",
          message: VALIDATION_IDS_MESSAGE,
        });

        return;
      }
    }

    updateMutation.mutate({
      ...program,
      previousContractorID:
        program.contractorID || program.previousContractorID || "",
      contractorID: trimmedValue,
      isEnrolled: true,
    });
  };

  const ActionButton = ({
    icon,
    label,
    onClick,
    isFullWidthOnMobile = true,
  }: {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    isFullWidthOnMobile?: boolean;
  }) => (
    <Button
      size="small"
      startIcon={icon}
      onClick={onClick}
      sx={{
        bgcolor: "white",
        color: "#0057B8",
        border: `1px solid #0057B8`,
        textTransform: "none",
        width: isFullWidthOnMobile ? { xs: "100%", lg: "auto" } : "auto",
        minWidth: isFullWidthOnMobile ? "auto" : "70px",
        px: isFullWidthOnMobile ? 1 : 1.5,
        fontSize: "0.7rem",
        height: "32px",
        "& .MuiButton-startIcon": {
          marginRight: label ? "4px" : "0px",
          "& svg": {
            color: "#0057B8",
          },
        },
      }}
    >
      {label}
    </Button>
  );

  const PrimaryActionButton = ({
    program,
    isEditing,
    isUpdating,
    openConfirmationModal,
    handleEnrollClick,
    handleUpdateSubmit,
    isEditingAnotherRow,
  }: {
    program: ContractorData;
    isEditing: boolean;
    isUpdating: boolean;
    openConfirmationModal: (program: ContractorData) => void;
    handleEnrollClick: (program: ContractorData) => void;
    handleUpdateSubmit: (program: ContractorData) => void;
    isEditingAnotherRow: boolean;
  }) => {
    const isEnrolled = program.isEnrolled;
    const hasPreviousId =
      !!program.previousContractorID && program.previousContractorID !== "";
    const buttonText = isEnrolled
      ? "UNENROLL"
      : hasPreviousId
      ? "REENROLL"
      : "ENROLL";

    const onClickHandler = () =>
      isEnrolled ? openConfirmationModal(program) : handleEnrollClick(program);

    let buttonSx: any = {
      minWidth: { xs: 80, sm: 90 },
      color: "white",
      fontWeight: "bold",
      fontSize: "0.65rem",
      textTransform: "uppercase",
      bgcolor: "#0057B8",
    };

    if (isEditing && !isEnrolled) {
      buttonSx = {
        ...buttonSx,
        background: STYLES.orangeGradient,
        border: "1px solid #d48600",
        "&:hover": { background: "#ff9a00" },
      };
    }

    return (
      <Button
        variant="contained"
        size="small"
        disableElevation
        sx={buttonSx}
        onClick={
          isEditing && !isEnrolled
            ? () => handleUpdateSubmit(program)
            : onClickHandler
        }
        disabled={isEditingAnotherRow || isUpdating}
      >
        {buttonText}
      </Button>
    );
  };

  const columns: Column<ContractorData>[] = [
    {
      id: "vendorProgram",
      label: "Vendor Program",
      sortable: false,
      align: "left",
      width: { xs: "220px", sm: "40%" },
      render: (program) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            py: 1,
          }}
        >
          <Box
            sx={{
              width: { xs: 80, sm: 120 },
              display: "flex",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <img
              src={
                require(`../../../img/logos/${program.vendor.logo}`) ??
                defaultImage
              }
              alt={program.programName}
              style={{
                maxHeight: "35px",
                maxWidth: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
          <Typography
            component="a"
            variant="body2"
            target="_blank"
            href={program.vendor.link}
            sx={{
              color: "#0057B8",
              fontWeight: 500,
              textDecoration: "none",
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              display: "block",
              wordBreak: "break-word",
              whiteSpace: "normal",
            }}
          >
            {program.vendor.programDisplayName}
          </Typography>
        </Box>
      ),
    },
    {
      id: "contractorID",
      label: "Contractor ID",
      sortable: false,
      align: "left",
      width: { xs: "150px", sm: "25%" },
      render: (program) => {
        const isEditing = editingId === program.id;
        return isEditing ? (
          <Tooltip
            title={
              VendorValidationHelper.getRuleDescription(
                program.vendor.program
              ) || ""
            }
            arrow
            placement="bottom"
            open={isEditing}
          >
            <TextField
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              size="small"
              autoFocus
              fullWidth
              sx={{
                bgcolor: "white",
                "& .MuiOutlinedInput-root": {
                  height: 35,
                  borderRadius: 0,
                  fontSize: "0.8rem",
                },
              }}
            />
          </Tooltip>
        ) : (
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "0.75rem", sm: "0.875rem" },
              whiteSpace: "normal",
              wordBreak: "break-all",
            }}
          >
            {program.contractorID || program.previousContractorID || ""}
          </Typography>
        );
      },
    },
    {
      id: "isEnrolled",
      label: "Currently Enrolled?",
      sortable: false,
      align: "left",
      width: { xs: "120px", sm: "15%" },
      render: (program) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            sx={{ width: 20, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {program.isEnrolled ? "Yes" : "No"}
          </Typography>
          <PrimaryActionButton
            program={program}
            isEditing={editingId === program.id}
            isUpdating={updateMutation.isPending}
            openConfirmationModal={setProgramToUnenroll}
            handleEnrollClick={(p) => {
              setEditingId(p.id);
              setCurrentInput(p.contractorID || p.previousContractorID || "");
            }}
            handleUpdateSubmit={handleUpdateSubmit}
            isEditingAnotherRow={editingId !== null && editingId !== program.id}
          />
        </Box>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      sortable: false,
      align: "center",
      width: { xs: "100px", sm: "20%" },
      render: (program) => {
        const isEditing = editingId === program.id;
        if (isEditing) {
          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 0.5,
                justifyContent: "center",
              }}
            >
              {program.isEnrolled && (
                <ActionButton
                  icon={<CheckIcon />}
                  label="Update"
                  onClick={() => handleUpdateSubmit(program)}
                />
              )}
              <ActionButton
                icon={<DoDisturbIcon />}
                label="Cancel"
                onClick={() => setEditingId(null)}
              />
            </Box>
          );
        }
        return program.isEnrolled ? (
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ActionButton
              isFullWidthOnMobile={false}
              icon={
                <EditIcon
                  sx={{ color: STYLES.linkBlue, fontSize: "1rem !important" }}
                />
              }
              label="Edit"
              onClick={() => {
                setEditingId(program.id);
                setCurrentInput(program.contractorID || "");
              }}
            />
          </Box>
        ) : null;
      },
    },
  ];

  const fetchEnrollments = async () => {
    const result = await dataService.getContractorData();
    const items = result.success ? result.data : [];
    return { items, total: items.length };
  };

  return (
    <Container
      disableGutters
      maxWidth={false}
      sx={{
        mt: 4,
      }}
    >
      {updateMutation.isPending && <PageLoadingOverlay text="Updating..." />}

      <Typography sx={{ mb: 2, px: { xs: 1, sm: 0 } }}>
        To enroll in Loyalty Link, you must already be signed up in a
        manufacturer's program and have your ID number. If you don't have your
        ID number, please look it up using the links below.
      </Typography>

      <GenericDataDisplay<ContractorData>
        queryKeyBase={["contractorEnrollments"]}
        fetchPage={fetchEnrollments}
        columns={columns}
        mode={GenericDataDisplayTypeEnum.TABLE}
        showPagination={false}
        cashingTime={SHORT_CASHING}
        externalFilters={[]}
      />

      <UnenrollModal
        program={programToUnenroll}
        onClose={() => setProgramToUnenroll(null)}
        onConfirm={(p) => updateMutation.mutate({ ...p, isEnrolled: false })}
      />
      {errorModal && (
        <SimpleModal
          isOpen={errorModal.isOpen}
          title={errorModal.title}
          message={errorModal.message}
          onClose={() => setErrorModal(null)}
        />
      )}
    </Container>
  );
};

export default EnrollmentTable;
